import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import TemplateTypes from '../../components/uisystem/templates/types';
import { Blank, IntroSplash, Title, EULA, Branch, FinishProcedure, Content, TitleMenu6, TitleMenu12, FullPageText, CustomProps, QuizResults } from '../../components/uisystem/templates';
import { assert } from '../../classes/utilities/ErrorHandling';
import { PreviewAspects } from '../../components/uisystem/PreviewAspect';
import APIUtility from '../../classes/APIUtility';
import { UISidebarContentTypes } from '../../globals';
import Scenario from '../../classes/scenarioElements/Scenario';
import { getUuidv4, traverseJSObject } from '../../classes/utilities/Misc';

const baseDisabledComponents = {
	Headers: {
		name: "Headers",
		type: "Headers",
		size: 4,
		anchor: "bottom",
		align: "left"
	},
	Insets: {
		name: "Insets",
		type: "Insets",
		size: 4
	},
	Options: {
		name: "Options",
		type: "Options",
		size: 6,
		top: 20
	},
	NextStep: {
		name: "Next Step",
		type: "NextStep",
	},
	PreviousStep: {
		name: "Previous Step",
		type: "PreviousStep",
	},
	ProjectOptions: {
		name: "Project Options",
		type: "ProjectOptions",
	},
	TextDescription: {
		name: "Text Description",
		type: "TextDescription",
	},
	Voice: {
		name: "Voice",
		type: "Voice",
	}
};

function syncStepUi(state, slideId, doublePass = false)
{
	const slideData = state.slideMaster[slideId];
	for (const [stepId, stepUi] of Object.entries<any>(state.ui))
	{
		if (stepUi.slide === slideId && slideData.active === stepUi.active && stepUi.synced === true)
		{
			//pass from the step side
			//console.log('sync ui pass 1')
			assert(stepUi[stepUi.active]);
			for (const [subcompKey, subcomp] of Object.entries<any>(stepUi[stepUi.active]))
			{
				for (const [propKey, prop] of Object.entries<any>(subcomp))
				{
					if (prop.type === 'group' || prop.type === 'camera') continue;
					slideIntegrityCheck(state, slideId, slideData.active, subcompKey, prop);
					if (!prop.changed || prop.value === slideData[stepUi.active][subcompKey][propKey].value)
					{
						const fullProp = { ...slideData[slideData.active][subcompKey][propKey], name: propKey };
						uiIntegrityCheck(state, stepId, stepUi.active, subcompKey, fullProp);
						state.ui[stepId][stepUi.active][subcompKey][propKey] = { ...fullProp, changed: false };
					}
				}
			}
			if (doublePass)
			{
				//console.log('sync ui pass 2')
				assert(slideData[slideData.active]);
				for (const [subcompKey, subcomp] of Object.entries<any>(slideData[slideData.active]))
				{
					for (const [propKey, prop] of Object.entries<any>(subcomp))
					{
						if (prop.type === 'group' || prop.type === 'camera') continue;
						const fullProp = { ...prop, name: propKey };
						uiIntegrityCheck(state, stepId, stepUi.active, subcompKey, fullProp);
						if (!state.ui[stepId][stepUi.active][subcompKey][propKey].changed)
						{
							state.ui[stepId][stepUi.active][subcompKey][propKey] = { ...slideData[slideData.active][subcompKey][propKey], name: propKey };
						}
					}
				}
			}
		}
		delete state.ui[stepId].name;
	}
}

function resetSlide(state, slideId)
{
	const slideData = state.slideMaster[slideId];
	if (!slideData || !slideData.active || !slideData[slideData.active]) return;

	for (const [subcompKey, subcomp] of Object.entries<any>(slideData[slideData.active]))
	{
		for (const [propKey, prop] of Object.entries<any>(subcomp))
		{
			state.slideMaster[slideId][slideData.active][subcompKey][propKey].value = state.slideMaster[slideId][slideData.active][subcompKey][propKey].default ?? "";
		}
	}
}

function createPremadeTemplate(state, type, name)
{
	//console.log('creating template '+type);
	if (!state.slideMaster[type])
	{
		state.slideMaster[type] = {};
		state.slideMaster[type].name = name;
		state.slideMaster[type].type = "slide";
		state.slideMaster[type].active = type;
	}

	let baseProperties = {};
	switch (type)
	{
		case TemplateTypes.BLANK:
			baseProperties = Blank({ getProperties: true });
			break;
		case TemplateTypes.BRANCH:
			baseProperties = Branch({ getProperties: true });
			break;
		case TemplateTypes.CONTENT:
			baseProperties = Content({ getProperties: true });
			break;
		case TemplateTypes.EULA:
			baseProperties = EULA({ getProperties: true });
			break;
		case TemplateTypes.FINISH_PROCEDURE:
			baseProperties = FinishProcedure({ getProperties: true });
			break;
		case TemplateTypes.FULL_PAGE_TEXT:
			baseProperties = FullPageText({ getProperties: true });
			break;
		case TemplateTypes.INTRO_SPLASH:
			baseProperties = IntroSplash({ getProperties: true });
			break;
		case TemplateTypes.TITLE:
			baseProperties = Title({ getProperties: true });
			break;
		case TemplateTypes.TITLE_MENU_12:
			baseProperties = TitleMenu12({ getProperties: true });
			break;
		case TemplateTypes.TITLE_MENU_6:
			baseProperties = TitleMenu6({ getProperties: true });
			break;
		case TemplateTypes.QUIZ_RESULTS:
			baseProperties = QuizResults({getProperties:true});
			break;
		case TemplateTypes.CUSTOM:
			delete state.slideMaster[type];
			return;
		case TemplateTypes.NONE:
			delete state.slideMaster[type];
			return;
		default:
			console.log("unrecognized type: ", type);
			return;
	}

	for (const [subCompKey, subComp] of Object.entries<any>(baseProperties))
	{
		for (const [propKey, prop] of Object.entries<any>(subComp))
		{
			prop.name = propKey;
			prop.value = prop.default ?? "";
			slideIntegrityCheck(state, type, type, subCompKey, prop);
		}
	}
	// console.log(state.slideMaster[type]);
}

function validateCustomSlideProperties(state, slide)
{
	//console.log('checking integrity of custom slide '+state.slideMaster[slide].name);
	const baseProperties = CustomProps({ getProperties: true, customMap: state.slideMaster[slide].customMap ?? [] });
	//console.log(baseProperties);
	for (const [subCompKey, subComp] of Object.entries<any>(baseProperties))
	{
		for (const [propKey, prop] of Object.entries<any>(subComp))
		{
			prop.name = propKey;
			prop.value = prop.default ?? "";
			slideIntegrityCheck(state, slide, TemplateTypes.CUSTOM, subCompKey, prop);
		}
	}
}

function slideIntegrityCheck(state, slide, template, subcomponent, property)
{
	//define slide key if it is undefined
	if (!state.slideMaster[slide])
	{state.slideMaster[slide] = {};}

	const slideCopy = JSON.parse(JSON.stringify(state.slideMaster[slide]));

	//define the template key if it is undefined
	if (!slideCopy[template])
	{slideCopy[template] = {};}
	state.slideMaster[slide] = slideCopy;

	//define the subcomponent key if it is undefined
	if (!slideCopy[template][subcomponent])
	{slideCopy[template][subcomponent] = {};}
	state.slideMaster[slide] = slideCopy;

	// define property it it is undefined
	if (!slideCopy[template][subcomponent][property.name])
	{
		slideCopy[template][subcomponent][property.name] = { ...property, value: property.default };
		state.slideMaster[slide] = slideCopy;
	}
}

function uiIntegrityCheck(state, step, template, subcomponent, property)
{
	//define step key if it is undefined
	if (!state.ui[step])
	{state.ui[step] = {};}

	//define the template key if it is undefined
	if (!state.ui[step][template])
	{state.ui[step][template] = {};}

	//define the subcomponent key if it is undefined
	if (!state.ui[step][template][subcomponent])
	{state.ui[step][template][subcomponent] = {};}

	// define property it it is undefined
	if (!state.ui[step][template][subcomponent][property.name])
	{state.ui[step][template][subcomponent][property.name] = { ...property };}
}

type UISliceState = {
	ui: any,
	slideMaster: any,
	previewVisible: boolean,
	tempUiPreview: boolean,
	previewScale: number,
	previewAspect: number,
	videoPlayerUrl: string | null,
	fullscreenAssetUrl: string,
	audioOnly: boolean,
	editingSlide: string,
	backupSlide: any,
	groupState: any,
	sidebarContent: UISidebarContentTypes,
	nextStepOverride: string,
};

const initialState: UISliceState = {
	ui: {},
	slideMaster: {},
	previewVisible: false,
	tempUiPreview: false,
	previewScale: 100,
	previewAspect: PreviewAspects.ASPECT_16_BY_9,
	videoPlayerUrl: null,
	fullscreenAssetUrl: "",
	audioOnly: false,
	editingSlide: "",
	backupSlide: null,
	groupState: {},
	sidebarContent: UISidebarContentTypes.None,
	nextStepOverride: "",
};

// TODO: fill in all the types!
const UISlice = createSlice({
	name: "ui",
	initialState: { ...initialState },
	reducers: {
		setProperty(state, action)
		{
			const { step, template, subcomponent, property, slide, breakSync } = action.payload;

			if (slide)
			{
				slideIntegrityCheck(state, slide, template, subcomponent, property);

				// set the subcomponent value
				state.slideMaster[slide][template][subcomponent][property.name] = { ...state.slideMaster[slide][template][subcomponent][property.name], ...property };
				syncStepUi(state, slide);
			}
			else
			{
				uiIntegrityCheck(state, step, template, subcomponent, property);
				const connectedSlideId = state.ui[step].slide;
				let connectedSlide: any = null;
				if (connectedSlideId) connectedSlide = state.slideMaster[connectedSlideId];
				if (connectedSlide) slideIntegrityCheck(state, connectedSlideId, template, subcomponent, property);

				// set the subcomponent value
				if (
					(connectedSlide) && //if step is connected to a template and template exists
					connectedSlide.active === state.ui[step].active //both the step and the template are using the same preset
				)
				{
					if (!connectedSlide[template] || !connectedSlide[template][subcomponent] || !connectedSlide[template][subcomponent][property.name]) return;
					if (
						property.value === connectedSlide[template][subcomponent][property.name].value || //values are the same
						(property.changed !== undefined && property.changed !== true) //or property should be synced to template
					)
					{
						state.ui[step][template][subcomponent][property.name] = { ...connectedSlide[template][subcomponent][property.name] };
						return;
					}
				}
				state.ui[step][template][subcomponent][property.name] = { ...state.ui[step][template][subcomponent][property.name], ...property, changed: breakSync && connectedSlideId ? true : false };
			}

		},
		setActiveTemplateForStep(state, action)
		{
			if (action.payload.slide)
			{
				// console.log('setting slide template');
				if (!state.slideMaster[action.payload.slide])
				{
					state.slideMaster[action.payload.slide] = {};
					state.slideMaster[action.payload.slide].name = "New UI Template";
					state.slideMaster[action.payload.slide].type = "slide";
				}

				if (action.payload.reset)
				{
					action.payload.template = state.slideMaster[action.payload.slide].active;
					resetSlide(state, action.payload.slide);
				}

				state.slideMaster[action.payload.slide].active = action.payload.template;
				if (action.payload.template === "Custom")
				{
					state.slideMaster[action.payload.slide].disabledComponents = { ...baseDisabledComponents };
					if (!state.slideMaster[action.payload.slide]["Custom"])
					{
						state.slideMaster[action.payload.slide]["Custom"] = {};
						state.slideMaster[action.payload.slide].customMap = [];
					}
				}

				for (const [key, value] of Object.entries<any>(state.ui))
				{
					if (value.slide === action.payload.slide && value.synced === true)
					{
						state.ui[key] = { ...state.slideMaster[action.payload.slide], slide: action.payload.slide, synced: true };
						delete state.ui[key].name;
					}
				}
			}
			else
			{
				if (!state.ui[action.payload.step])
				{state.ui[action.payload.step] = {};}

				state.ui[action.payload.step].active = action.payload.template;
				state.ui[action.payload.step].synced = false;
				if (action.payload.template === "Custom")
				{
					state.ui[action.payload.step].disabledComponents = { ...baseDisabledComponents };
				}
			}

		},
		setUIPreviewVisible(state, action)
		{
			state.previewVisible = action.payload.value;
		},
		setTempUiPreview(state, action)
		{
			state.tempUiPreview = action.payload;
		},
		setUIPreviewScale(state, action)
		{
			state.previewScale = action.payload.value;
			APIUtility.saveWlcSettings();
		},
		setUIPreviewAspectRatio(state, action)
		{
			state.previewAspect = action.payload.value;
			APIUtility.saveWlcSettings();
		},
		loadUIData(state, action)
		{
			state.ui = action.payload;
			state.groupState = {};

			// remove data from non-existent steps
			for(const stepId of Object.keys(state.ui))
			{
				if(!Scenario.getStep(stepId))
				{
					delete state.ui[stepId];
				}
			}
		},
		showVideoPlayer(state, action)
		{
			state.videoPlayerUrl = action.payload.visible ? action.payload.url : null;
			state.audioOnly = action.payload.audioOnly === true ? true : false;
		},
		setFromCopy(state, action)
		{
			state.ui[action.payload.stepId] = action.payload.content;
		},
		setSlideFromCopy(state, action)
		{
			if (!action.payload.slide)
			{
				action.payload.slide = getUuidv4();
			}

			state.slideMaster[action.payload.slide] = action.payload.content;
			if (!state.slideMaster[action.payload.slide].name)
			{
				state.slideMaster[action.payload.slide].name = action.payload.name ?? action.payload.slide;
			}
			if (action.payload.sourceStepId)
			{
				state.ui[action.payload.sourceStepId].slide = action.payload.slide;
				state.ui[action.payload.sourceStepId].synced = true;
			}

			syncStepUi(state, action.payload.slide);
		},
		loadSlideMasterData(state, action)
		{
			if (action.payload.shouldConvert)
			{
				const tempSlideMaster = {};
				for (const [key, value] of Object.entries<any>(action.payload.ui))
				{
					if (!value || Object.keys(value).length === 0) continue;
					if (value && value.active === TemplateTypes.NONE) continue;
					const uiEntryId = getUuidv4();
					tempSlideMaster[uiEntryId] = { ...value };
					for (const [skey, svalue] of Object.entries<any>(action.payload.shouldConvert.sequences))
					{
						if (svalue?.stepIds.includes(key))
						{
							tempSlideMaster[uiEntryId].name = `(${svalue.name}).(${action.payload.shouldConvert.steps[key].name})_template`;
							break;
						}
					}
					tempSlideMaster[uiEntryId].type = 'slide';
					value.slide = uiEntryId;
					value.synced = true;
				}
				state.slideMaster = tempSlideMaster;
				state.ui = action.payload.ui;
			}
			else
			{
				// console.log('loading ui without conversion');
				state.slideMaster = action.payload.ui;
			}

			for (const key of Object.keys(TemplateTypes))
			{
				if (TemplateTypes[key] === TemplateTypes.NONE || TemplateTypes[key] === TemplateTypes.CUSTOM) continue;
				createPremadeTemplate(state, TemplateTypes[key], TemplateTypes[key]);
			}

			for (const s of Object.keys(state.slideMaster))
			{
				if (state.slideMaster[s].active === TemplateTypes.CUSTOM)
				{
					validateCustomSlideProperties(state, s);
				}
				syncStepUi(state, s, true);
			}
		},
		deleteSlide(state, action)
		{
			delete state.slideMaster[action.payload.id];
		},
		deleteAllSlides(state, action: PayloadAction<undefined>)
		{
			state.slideMaster = {};
		},
		renameSlide(state, action)
		{
			state.slideMaster[action.payload.id].name = action.payload.name;
		},
		moveItem(state, action)
		{
			if (action.payload.slide)
			{
				const newArray = state.slideMaster[action.payload.slide].customMap.filter((item, idx) => idx !== action.payload.itemIndex);
				newArray.splice(action.payload.index, 0, { ...state.slideMaster[action.payload.slide].customMap[action.payload.itemIndex] });
				state.slideMaster[action.payload.slide].customMap = newArray;
				const slide = action.payload.slide;
				for (const [key, value] of Object.entries<any>(state.ui))
				{
					if (value.slide === slide && value.synced === true)
					{
						state.ui[key] = { ...state.slideMaster[slide], slide: slide, synced: true };
						delete state.ui[key].name;
					}
				}
			}
			else
			{
				const newArray = state.ui[action.payload.step].customMap.filter((item, idx) => idx !== action.payload.itemIndex);
				newArray.splice(action.payload.index, 0, { ...state.ui[action.payload.step].customMap[action.payload.itemIndex] });
				state.ui[action.payload.step].customMap = newArray;
				state.ui[action.payload.step].synced = false;
			}
		},
		addItem(state, action)
		{
			if (!action.payload.item) return;
			if (action.payload.slide)
			{
				const slide = action.payload.slide;
				if (!state.slideMaster[slide].customMap)
				{
					state.slideMaster[slide].customMap = [];
				}
				state.slideMaster[slide].customMap.push(action.payload.item);
				validateCustomSlideProperties(state, slide);
				for (const [key, value] of Object.entries<any>(state.ui))
				{
					if (value.slide === slide && value.synced === true)
					{
						state.ui[key] = { ...state.slideMaster[slide], slide: slide, synced: true };
						delete state.ui[key].name;
					}
				}
			}
			else
			{
				if (!state.ui[action.payload.step].customMap)
				{
					state.ui[action.payload.step].customMap = [];
				}
				state.ui[action.payload.step].customMap.push(action.payload.item);
				state.ui[action.payload.step].synced = false;
			}
		},
		removeItem(state, action)
		{
			if (action.payload.slide)
			{
				const itemType = state.slideMaster[action.payload.slide].customMap[action.payload.itemIndex].type;
				delete state.slideMaster[action.payload.slide]["Custom"][itemType];
				state.slideMaster[action.payload.slide].customMap.splice(action.payload.itemIndex, 1);
				const slide = action.payload.slide;
				for (const [key, value] of Object.entries<any>(state.ui))
				{
					if (value.slide === slide && value.synced === true)
					{
						state.ui[key] = { ...state.slideMaster[slide], slide: slide, synced: true };
						delete state.ui[key].name;
					}
				}
			}
			else
			{
				const itemType = state.ui[action.payload.step].customMap[action.payload.itemIndex].type;
				delete state.ui[action.payload.step]["Custom"][itemType];
				state.ui[action.payload.step].customMap.splice(action.payload.itemIndex, 1);
				state.ui[action.payload.step].synced = false;
			}
		},
		editItem(state, action)
		{
			if (action.payload.slide)
			{
				state.slideMaster[action.payload.slide].disabledComponents[action.payload.type][action.payload.property] = action.payload.value;
				for (let i = 0; i < state.slideMaster[action.payload.slide].customMap.length; i++)
				{
					const currentItem = state.slideMaster[action.payload.slide].customMap[i];
					if (currentItem.type === action.payload.type)
					{
						state.slideMaster[action.payload.slide].customMap[i][action.payload.property] = action.payload.value;
						const slide = action.payload.slide;
						for (const [key, value] of Object.entries<any>(state.ui))
						{
							if (value.slide === slide && value.synced === true)
							{
								state.ui[key] = { ...state.slideMaster[slide], slide: slide, synced: true };
								delete state.ui[key].name;
							}
						}
						break;
					}
				}
			}
			else
			{
				state.ui[action.payload.step].disabledComponents[action.payload.type][action.payload.property] = action.payload.value;
				for (let i = 0; i < state.ui[action.payload.step].customMap.length; i++)
				{
					const currentItem = state.ui[action.payload.step].customMap[i];
					if (currentItem.type === action.payload.type)
					{
						state.ui[action.payload.step].customMap[i][action.payload.property] = action.payload.value;
						state.ui[action.payload.step].synced = false;
						break;
					}
				}
			}
		},
		setEditingSlide(state, action)
		{
			state.editingSlide = action.payload;
		},
		setBackupSlide(state, action)
		{
			state.backupSlide = action.payload;
		},
		setGroupState(state, action)
		{
			if (!state.groupState[action.payload.location])
			{
				state.groupState[action.payload.location] = {};
			}

			state.groupState[action.payload.location][action.payload.key] = action.payload.value;
		},
		setSidebarContent(state, action)
		{
			state.sidebarContent = action.payload;
		},
		replaceAsset(state, action)
		{
			const { oldId, newId } = action.payload;
			const oldUI = { ...state.ui };
			const oldSlideMaster = { ...state.slideMaster };
			traverseJSObject(oldUI, (object, key, value) => { if(value === oldId) object[key] = newId });
			traverseJSObject(oldSlideMaster, (object, key, value) => { if(value === oldId) object[key] = newId });
			state.ui = { ...oldUI };
			state.slideMaster = { ...oldSlideMaster };
		},
		setNextStepOverride(state, action)
		{
			state.nextStepOverride = action.payload;
		},
		removeUIFromStep(state, action)
		{
			delete state.ui[action.payload];
		},
		setFullscreenAssetUrl(state, action)
		{
			state.fullscreenAssetUrl = action.payload;
		}
	},
});

export default UISlice;